import { Fragment, HTMLProps, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import { CategoryWithParts, IPart } from "../../../../models/Part";
import { ChevronRightIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import classNames from "../../../../utils/classNames";
import LeadPartsCategoriesTableSubRow from "./LeadPartsCategoriesTableSubRow";
import { DateTime } from "luxon";
import LoadingPulse from "../../../shared/LoadingPulse";
import { LastSoldPriceButton, SmartPriceButton, PartProPriceButton } from "../../../shared/PriceButtons";
import formatDateTime from "../../../../utils/formatDateTime";
import EbayLinkButton from "../../../shared/Inputs/EbayLinkButton";
import { IVehicle } from "../../../../models/Vehicle";
import TableCheckboxCol from "../../../shared/Tables/IndeterminateCheckbox";

const colHelper = createColumnHelper<CategoryWithParts>();

function IndeterminateCheckbox({
  indeterminate,
  className = "",
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + "p-3 cursor-pointer"}
      {...rest}
    />
  );
}


function EditablePriceCell({ rowId, value, handleSetTableData }: { rowId: string; value: number; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localPriceValue, setLocalPriceValue] = useState<number>(value);

  // Whenever the parent value changes (e.g. clicking the Suggested Price button),
  // update localValue so the input shows the latest.
  useEffect(() => {
    setLocalPriceValue(value);
  }, [value]);

  return (
    <input
      type="number"
      value={localPriceValue}
      min={0}
      onChange={(e) => {
        // If user clears the field, treat as 0
        const raw = e.target.value;
        const parsed = raw === "" ? 0 : parseFloat(raw);
        setLocalPriceValue(Number.isNaN(parsed) ? 0 : parsed);
      }}
      onBlur={() => {
        // Commit back into tableData on blur
        if (localPriceValue !== value) {
          handleSetTableData(rowId, localPriceValue);
        }
      }}
      className="border p-1 w-full"
    />
  );
}

function EditableNoteCell({ rowId, value, handleSetTableData }: { rowId: string; value: string; handleSetTableData: (rowId: string, price: number | undefined, note?: string | undefined) => void; }) {
  const [localNoteValue, setLocalNoteValue] = useState<string>(value);

  // Keep local state in sync if parent value changes (e.g., external updates)
  useEffect(() => {
    setLocalNoteValue(value ?? "");
  }, [value]);

  return (
    <textarea
      value={localNoteValue}
      rows={1}
      onChange={(e) => {
        setLocalNoteValue(e.target.value);
      }}
      onBlur={() => {
        // Commit back into tableData on blur
        if (localNoteValue !== value) {
          handleSetTableData(rowId, undefined, localNoteValue);
        }
      }}
      className="border p-1 text-sm w-full"
    />
  );
}

export interface LeadPartsCategoriesTableProps {
  categories: CategoryWithParts[];
  selectedCategories?: CategoryWithParts[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<CategoryWithParts[]>>;
  partProUnlocked: boolean;
  partProLoading: boolean;
  vehicle: IVehicle;
}

export default function LeadPartsCategoriesTable({ categories, selectedCategories, setSelectedCategories, partProUnlocked, partProLoading, vehicle }: LeadPartsCategoriesTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [tableData, setTableData] = useState<CategoryWithParts[]>(categories);

  const rowSelection = useMemo(() => {
    const sel: Record<string, boolean> = {};
    selectedCategories?.forEach((cat) => {
      sel[cat.category.id] = true;
    });
    return sel;
  }, [selectedCategories]);

  const handleSetTableData = useCallback((rowId: string, newPrice: number | undefined, newNote: string | undefined = undefined) => {
    setTableData((old) => {
      let updatedRow: CategoryWithParts | undefined;

      const newArray = old.map((item) => {
        if (item.category.id === rowId) {
          updatedRow = { ...item };

          if (typeof newPrice === "number" && !Number.isNaN(newPrice)) {
            updatedRow.price = newPrice;
          }
          if (typeof newNote !== "undefined") {
            // Allow empty string to clear the note intentionally
            updatedRow.note = newNote;
          }

          return updatedRow;
        }
        return item;
      });

      //This function is called here with an updater, but goes all the way back up to Lead Parts
      setSelectedCategories((prevSelectedForThisTable) => {
        const isCurrentlySelected = prevSelectedForThisTable.some(
          (cat) => cat.category.id === rowId
        );

        // If a numeric price was provided, handle selection add/update/remove based on price
        if (typeof newPrice === "number" && !Number.isNaN(newPrice)) {
          if (newPrice <= 0 && isCurrentlySelected) {
            // Remove from selection if price is now invalid
            return prevSelectedForThisTable.filter((cat) => cat.category.id !== rowId);
          }

          if (newPrice > 0 && updatedRow) {
            if (!isCurrentlySelected) {
              // If it was not selected, add it
              return [...prevSelectedForThisTable, updatedRow];
            } else {
              // If it was selected, update it
              return prevSelectedForThisTable.map((cat) =>
                cat.category.id === rowId ? updatedRow! : cat
              );
            }
          }
        }

        // If only the note changed, propagate the updated note into the selected item
        if (typeof newPrice !== "number" && typeof newNote !== "undefined" && updatedRow && isCurrentlySelected) {
          return prevSelectedForThisTable.map((cat) =>
            cat.category.id === rowId ? updatedRow! : cat
          );
        }

        // Otherwise, no change
        return prevSelectedForThisTable;
      });

      return newArray;
    });
  }, [setSelectedCategories]);

  const makeModelString = useMemo(() => {
    let string = "";

    if (vehicle?.dvlaYearOfManufacture) {
      string = vehicle?.dvlaYearOfManufacture + "+";
    }

    string += vehicle?.combinedMake;
    string += "+";
    string += vehicle?.modelRangeDescription;

    return string;
  }, [vehicle])

  const cols = useMemo(
    () => [
      {
        id: 'dropdown',
        cell: ({ row }: any) => (
          <div className="flex justify-center items-center flex-1 p-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => { e.stopPropagation(); row.toggleExpanded(); }}
              style={{ transform: `rotate(${!row.getIsExpanded() ? "0" : "-270"}deg)` }}
              className="p-1 transition duration-500 cursor-pointer border border-gray-400 rounded-full hover:bg-gray-200"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        ),
      },
      colHelper.accessor("category.name", {
        id: "category.name",
        header: "Name",
        cell: (info) =>
          <div className="flex gap-x-2 items-center justify-between">
            <p>{info.getValue()}</p>
            <EbayLinkButton query={makeModelString + "+" + info.getValue()} />
          </div>,
      }),
      colHelper.display({
        id: "lookupMpn",
        header: "MPN",
        cell: (info) => {
          if (partProLoading) return <LoadingPulse width="w-full" />;

          let lookupPartNumber = info.row.original.category.lookupPartNumber;
          return (
            <span>{partProUnlocked ?
              (
                <div className="flex gap-x-2 items-center justify-between ">
                  <span>{lookupPartNumber ?? "-"}</span>
                  {lookupPartNumber && <EbayLinkButton query={lookupPartNumber} />}
                </div>
              ) :
              (<span><LockClosedIcon height={15} width={15} /></span>)
            }</span>
          );
        }
      }),
      colHelper.accessor(
        row => row.parts.filter(part => !part.soldDate).length, //currently in stock, sat on the shelf
        {
          id: "partsCount",
          header: "Stock",
          cell: (info) => {
            const partsCount = info.getValue() as number;
            const partProCount = 0; //don't have this data yet
            return (
              <div>
                <p>
                  <span className="text-gray-600 text-sm">
                    Internal:{" "}
                  </span>
                  <span>
                    {partsCount > 0 ? partsCount : "-"}
                  </span>
                </p>
                <p className="flex items-center gap-x-1">
                  <span className="text-gray-600 text-sm">
                    Part Pro:{" "}
                  </span>
                  {partProLoading ? (
                    <span className="-mr-10">
                      <LoadingPulse width="w-10" />
                    </span>
                  ) : partProUnlocked ? (
                    <span className="text-wilma-500 font-semibold">
                      {partProCount > 0 ? partProCount : "-"}
                    </span>
                  ) : (
                    <span className=""><LockClosedIcon height={15} width={15} /></span>
                  )}
                </p>
              </div>
            );
          }
        }),
      colHelper.accessor(
        row => row.parts?.filter(x => x.soldDate)?.length, //total sold from matched parts list
        {
          id: "soldQuantity",
          header: "Total Previously Sold",
          cell: (info) => {
            var lastSold = info.row.original.parts?.filter(x => x.soldDate)?.sort((a, b) => (a.soldDate! < b.soldDate! ? 1 : -1))[0]?.soldDate;
            return (
              <div>
                <p>{info.getValue() ?? "-"}</p>
                {lastSold && (<p className="text-gray-600 text-sm">Last Sold: {formatDateTime(lastSold, DateTime.DATE_MED)}</p>)}
              </div>
            )
          }
        }),
      colHelper.accessor("note",
        {
          id: "note",
          header: "Internal Note",
          enableSorting: false,
          cell: (info) => {
            const currentValue = info.getValue();
            const rowId = info.row.original.category.id;
            return (
              <EditableNoteCell
                rowId={rowId}
                value={currentValue ?? ""}
                handleSetTableData={handleSetTableData}
              />
            );
          }
        }),
      colHelper.accessor(
        (row) => row.parts.find((part: IPart) => part.soldDate)?.price || 0,
        {
          id: "lastSoldPriceButton",
          header: "Last Sold",
          cell: (info) => {
            const row = info.row.original;
            const rowId = row.category.id;

            return (
              <LastSoldPriceButton
                parts={row.parts}
                onPriceSelect={(price) => handleSetTableData(rowId, price)}
              />
            );
          },
        }
      ),
      colHelper.accessor(
        (row) => {
          const basePrice = row.parts.find((part: IPart) => part.soldDate)?.price || 0;
          return parseFloat((basePrice * 1.1).toFixed(2));
        },
        {
          id: "marketPriceButton",
          header: "Market",
          cell: (info) => {
            const row = info.row.original;
            const rowId = row.category.id;

            return (
              <SmartPriceButton
                categoryWithParts={row}
                onPriceSelect={(price) => handleSetTableData(rowId, price)}
              />
            );
          },
        }
      ),
      colHelper.accessor((row) => {
        const basePrice = row.category.lookupAveragePrice || 0;
        return parseFloat((basePrice).toFixed(2));
      },
        {
          id: "partProPriceButton",
          header: "PartPro",
          cell: (info) => {
            const row = info.row.original;
            const rowId = row.category.id;

            return (
              <PartProPriceButton
                category={row.category}
                onPriceSelect={(price) => handleSetTableData(rowId, price)}
                partProUnlocked={partProUnlocked}
                partProLoading={partProLoading}
              />
            );
          },
        }
      ),
      colHelper.accessor((row) => row.price, {
        header: "Price",
        sortingFn: "auto",
        // Use our EditablePriceCell component
        cell: (info) => {
          const currentValue = info.getValue() as number;
          const rowId = info.row.original.category.id;
          return (
            <EditablePriceCell
              rowId={rowId}
              value={currentValue}
              handleSetTableData={handleSetTableData}
            />
          );
        },
      }),
      TableCheckboxCol(false),
    ],
    [handleSetTableData, partProLoading, partProUnlocked] // only re‐compute columns if the callback changes
  );

  const table = useReactTable({
    data: tableData,
    columns: cols,
    getRowId: (row) => row.category.id,
    state: {
      sorting: sorting,
      rowSelection: rowSelection
    },
    enableRowSelection: true,
    onRowSelectionChange: (updaterOrState) => {
      // If it's a function, call it with the current state to get newState.
      const newState: Record<string, boolean> =
        typeof updaterOrState === "function"
          ? updaterOrState(rowSelection)
          : updaterOrState;

      const newlySelected: CategoryWithParts[] = [];
      for (const [rowId, isSel] of Object.entries(newState)) {
        if (isSel) {
          const found = tableData.find((c) => c.category.id === rowId);
          if (found) newlySelected.push(found);
        }
      }
      setSelectedCategories(newlySelected);
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setTableData(categories);
  }, [categories]);

  return (
    <>
      <div className="mt-4 bg-white overflow-auto space-y-4">
        <table className="min-w-full overflow-scroll divide-y border divide-gray-300">

          {/* Table headings - using nth child to make the name column the same width */}
          {/* Also using first and last to make sure the dropdown and select boxes are the same width */}
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800 [&>*:nth-child(2)]:w-36 [&>*:nth-child(7)]:w-20 [&>*:nth-child(8)]:w-20 [&>*:nth-child(9)]:w-20 [&>*:nth-child(9)]:text-wilma-500 [&>*:nth-child(9)]:font-bold" >
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="flex first:w-16 last:w-16 py-1 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Body */}
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              let noPriceSelected = row.getIsSelected() === true && (!row.original.price || row.original.price <= 0);

              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    // onClick={() => setLocation(row.original)}
                    className={classNames(noPriceSelected ? "bg-red-200" : "", `hover:bg-gray-100`)}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      {/* 2nd row is a custom 1 cell row */}
                      <td colSpan={row.getVisibleCells().length}>
                        <LeadPartsCategoriesTableSubRow categoryWithParts={row.original} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>

        </table>
      </div>
    </>
  );
}
